import { User } from '@garbera/shared';

// 学歴状況の定義（共有パッケージのビルド問題回避のため）
export enum GraduationStatus {
  CURRENT = 'current',
  EXPECTED = 'expected',
  GRADUATED = 'graduated',
}

export interface ProfileFormData {
  fullName: string;
  email: string;
  bio?: string;
  phone?: string;
  organization?: string;
  avatarUrl?: string;
}

export interface ProfileUpdateData {
  fullName?: string;
  furigana?: string;
  gender?: string;
  bio?: string;
  phone?: string;
  organization?: string;
  // 営業実績・経験情報
  mediationCount?: number;
  monthlyMaxSales?: number;
  totalSales?: number;
  realEstateExperienceMonths?: number;
  entrepreneurialInterest?: boolean;
  // キャリア・希望情報
  currentPrefecture?: string;
  desiredPrefecture?: string;
  qualifications?: string[];
  desiredJobType?: string;
  // 学歴情報
  birthDate?: string; // ISO 8601 date string
  university?: string;
  faculty?: string;
  graduationYear?: number;
  graduationStatus?: GraduationStatus;
}

// 学歴情報用の型定義
export interface EducationInfo {
  birthDate?: string;
  university?: string;
  faculty?: string;
  graduationYear?: number;
  graduationStatus?: GraduationStatus;
}

export interface GraduationStatusOption {
  value: GraduationStatus;
  label: string;
}

// 学歴情報の選択肢
export const GRADUATION_STATUS_OPTIONS: GraduationStatusOption[] = [
  { value: GraduationStatus.CURRENT, label: '在学中' },
  { value: GraduationStatus.EXPECTED, label: '卒業予定' },
  { value: GraduationStatus.GRADUATED, label: '卒業済み' },
];

export interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UserSettings {
  notifications: {
    email: boolean;
    push: boolean;
    chatUpdates: boolean;
    systemUpdates: boolean;
  };
  privacy: {
    profileVisible: boolean;
    activityVisible: boolean;
  };
  preferences: {
    language: 'ja' | 'en';
    theme: 'light' | 'dark' | 'auto';
    timezone: string;
  };
}

export interface AvatarUploadResult {
  url: string;
  fileSize: number;
  fileName: string;
}

export interface FileUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface FileValidationError {
  type: 'size' | 'format' | 'network' | 'unknown';
  message: string;
}

export interface ProfileState {
  user: User | null;
  settings: UserSettings | null;
  isLoading: boolean;
  isUpdating: boolean;
  isUploadingAvatar: boolean;
  error: string | null;
  avatarError: string | null;
  uploadProgress: FileUploadProgress | null;
  successMessage: string | null;
}

export interface ProfileContextType extends ProfileState {
  updateProfile: (data: ProfileUpdateData) => Promise<void>;
  updateSettings: (settings: Partial<UserSettings>) => Promise<void>;
  changePassword: (data: PasswordChangeData) => Promise<void>;
  uploadAvatar: (file: File) => Promise<AvatarUploadResult>;
  clearError: () => void;
  clearAvatarError: () => void;
  clearSuccessMessage: () => void;
  refreshProfile: () => Promise<void>;
}

// Constants for validation
export const AVATAR_CONSTRAINTS = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_FORMATS: ['image/jpeg', 'image/png', 'image/webp'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp'],
} as const;

export const VALIDATION_MESSAGES = {
  FILE_SIZE_EXCEEDED: 'ファイルサイズは5MB以下にしてください',
  INVALID_FORMAT: 'JPEGまたはPNG形式の画像を選択してください',
  UPLOAD_FAILED: 'アップロードに失敗しました。再度お試しください',
  NETWORK_ERROR: 'ネットワークエラーが発生しました',
} as const;